/**
 * The CCS Approach — Brand logo and lockup components.
 * Source image: 757×419 PNG with the gold-ringed emblem on the left
 * (figures + C·C·S letters), wordmark on the right.
 *
 * LogoMark crops to just the circular emblem so it can be used as a brand badge.
 */
import { Link } from "react-router-dom";

export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ccs-consult/artifacts/hzn1wx4q_image.png";

// Emblem region inside the 757×419 source (calibrated by hand)
const EMBLEM_W = 230;
const EMBLEM_CX = 250;
const EMBLEM_CY = 210;
const IMG_W = 757;
const IMG_H = 419;

/** Precisely-cropped circular emblem from the source PNG (measured: center 148,220 · ring Ø190).
 * Size-agnostic (uses % positioning) — safe at any display size, unlike LogoMark's px calibration. */
export const EmblemImage = ({ className = "", testid = "emblem-image" }) => (
  // Crop window Ø210 source-px centered on the emblem → image width = 757/210 = 360.5%
  <div
    className={`relative rounded-full overflow-hidden ${className}`}
    data-testid={testid}
    role="img"
    aria-label="The CCS Approach emblem"
  >
    <img
      src={LOGO_URL}
      alt=""
      draggable={false}
      style={{
        position: "absolute",
        width: "360.5%",
        maxWidth: "none",
        left: "-20.5%", // (0.5 − 148/210) × 100
        top: "-54.8%",  // (0.5 − 220/210) × 100
      }}
    />
  </div>
);

/** Just the circular emblem — for compact branding spots. */
export const LogoMark = ({ size = 44, className = "", testid = "logo-mark" }) => {
  // Scale so the emblem width matches the desired badge size
  const sf = size / EMBLEM_W;
  const bgW = Math.round(IMG_W * sf);
  const bgH = Math.round(IMG_H * sf);
  const ox = Math.round(size / 2 - EMBLEM_CX * sf);
  const oy = Math.round(size / 2 - EMBLEM_CY * sf);
  return (
    <div
      data-testid={testid}
      className={`shrink-0 rounded-full overflow-hidden bg-white shadow-[0_2px_10px_rgba(93,58,31,0.18)] ring-2 ring-[#C28A2E] ${className}`}
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${LOGO_URL})`,
        backgroundSize: `${bgW}px ${bgH}px`,
        backgroundPosition: `${ox}px ${oy}px`,
        backgroundRepeat: "no-repeat",
      }}
      aria-label="The CCS Approach"
      role="img"
    />
  );
};

/** Emblem + wordmark for header bars and login screens. */
export const LogoLockup = ({
  size = 44,
  onLight = true,
  link = false,
  testid = "logo-lockup",
}) => {
  const titleColor = onLight ? "text-[#2A1810]" : "text-white";
  const sublabelColor = onLight ? "text-[#6B5944]" : "text-[#F0E6D2]/65";

  const inner = (
    <div className="flex items-center gap-3" data-testid={testid}>
      <LogoMark size={size} />
      <div className="leading-tight">
        <div
          className={`font-display font-semibold tracking-tight ${titleColor}`}
          style={{ fontSize: size >= 48 ? "1.25rem" : "1.05rem" }}
        >
          The CCS Approach
        </div>
        <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${sublabelColor}`}>
          Communicate · Collaborate · Serve
        </div>
      </div>
    </div>
  );

  if (link) return <Link to="/" className="group">{inner}</Link>;
  return inner;
};

/** Full logo image (emblem + wordmark) — for hero and print contexts. */
export const LogoFull = ({ maxWidth = 320, className = "", testid = "logo-full" }) => (
  <img
    src={LOGO_URL}
    alt="The CCS Approach — Communicate · Collaborate · Serve"
    data-testid={testid}
    className={`block h-auto ${className}`}
    style={{ maxWidth, width: "100%" }}
    draggable={false}
  />
);

export default LogoLockup;
